import { Fragment, useEffect, useMemo, useState } from "react";
import { fetchWithAuth } from "../../../utils/fetchWithAuth";
import { API_BASE } from "../../../config";
import MeasuredChartFrame from "./MeasuredChartFrame";

import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";

const TICK = { fill: "#d6d5ea", fontSize: 12 };

const COLORS = {
    TOTAL: "#EDEBFF",
    ENOW: "#52EA91",
    FG: "#FFB347",
    FIELDGLASS: "#FFB347",
    UNKNOWN: "#6F3AFF",
};

function toNumber(value, fallback = 0) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function safeKey(value) {
    return String(value || "UNKNOWN")
        .trim()
        .replace(/[^a-zA-Z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toUpperCase();
}

function normalizeManagedByName(value) {
    const raw = String(value || "UNKNOWN").trim();

    if (/^fg$/i.test(raw)) return "FG";
    if (/fieldglass/i.test(raw)) return "FIELDGLASS";
    if (/enow/i.test(raw)) return "ENOW";
    if (/total/i.test(raw)) return "TOTAL";

    return raw.toUpperCase();
}

function formatDate(dateStr) {
    if (!dateStr) return "";

    const d = new Date(`${dateStr}T00:00:00`);

    if (Number.isNaN(d.getTime())) {
        return String(dateStr);
    }

    return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
    });
}

function formatInt(value) {
    return Number(value ?? 0).toLocaleString("de-DE");
}

function getOpenClosed(value) {
    return {
        open: toNumber(
            value?.OPEN ??
            value?.open ??
            value?.open_cases ??
            value?.openCases ??
            value?.current?.OPEN,
            0
        ),
        closed: toNumber(
            value?.CLOSED ??
            value?.closed ??
            value?.closed_cases ??
            value?.closedCases ??
            value?.current?.CLOSED,
            0
        ),
    };
}

function getRowsArray(raw) {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.rows)) return raw.rows;
    if (Array.isArray(raw?.items)) return raw.items;
    return [];
}

function normalizeTypeOptions(raw) {
    return getRowsArray(raw)
        .map(
            (row) =>
                row.remediation_type ||
                row.REMEDIATION_TYPE ||
                row.type_name ||
                row.TYPE_NAME ||
                row.name
        )
        .filter(Boolean)
        .map(String);
}

function normalizeFlatRows(rows) {
    const byDate = new Map();
    const managedByNames = new Set();

    rows.forEach((row) => {
        const date = row.date ?? row.day ?? row.period ?? row.label;
        const managedBy = normalizeManagedByName(
            row.managed_by ?? row.MANAGED_BY ?? row.source ?? row.system
        );

        if (!date || !managedBy) return;

        const rowState = byDate.get(date) || {
            date,
            OPEN__TOTAL: 0,
            CLOSED__TOTAL: 0,
        };

        const { open, closed } = getOpenClosed(row);
        const key = safeKey(managedBy);

        rowState[`OPEN__${key}`] = open;
        rowState[`CLOSED__${key}`] = closed;
        rowState.OPEN__TOTAL += open;
        rowState.CLOSED__TOTAL += closed;

        managedByNames.add(managedBy);
        byDate.set(date, rowState);
    });

    return {
        rows: Array.from(byDate.values()),
        managedByNames: Array.from(managedByNames),
    };
}

function normalizeNestedRows(rows) {
    const managedByNames = new Set();

    const normalizedRows = rows.map((row) => {
        const date = row.date ?? row.day ?? row.period ?? row.label;
        const byManagedBy =
            row.managed_by ??
            row.by_managed_by ??
            row.by_source ??
            row.sources ??
            row.systems ??
            {};

        const nextRow = {
            date,
            OPEN__TOTAL: 0,
            CLOSED__TOTAL: 0,
        };

        Object.entries(byManagedBy).forEach(([managedByRaw, value]) => {
            const managedBy = normalizeManagedByName(managedByRaw);
            const { open, closed } = getOpenClosed(value);
            const key = safeKey(managedBy);

            nextRow[`OPEN__${key}`] = open;
            nextRow[`CLOSED__${key}`] = closed;
            nextRow.OPEN__TOTAL += open;
            nextRow.CLOSED__TOTAL += closed;

            managedByNames.add(managedBy);
        });

        const total = row.total ?? row.TOTAL ?? row.all ?? null;

        if (total) {
            const { open, closed } = getOpenClosed(total);
            nextRow.OPEN__TOTAL = open;
            nextRow.CLOSED__TOTAL = closed;
        }

        return nextRow;
    });

    return {
        rows: normalizedRows,
        managedByNames: Array.from(managedByNames),
    };
}

function normalizePayload(raw) {
    const rows = getRowsArray(raw);

    const hasFlatShape = rows.some((row) =>
        [row?.managed_by, row?.MANAGED_BY, row?.source, row?.system].some(
            (value) => typeof value === "string"
        )
    );

    return hasFlatShape ? normalizeFlatRows(rows) : normalizeNestedRows(rows);
}

function getSeriesColor(name) {
    return COLORS[safeKey(name)] || COLORS.UNKNOWN;
}

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;

    return (
        <div className="remediation-dashboard-tooltip">
            <div className="remediation-dashboard-tooltip-title">
                {formatDate(label)}
            </div>

            {payload.map((point) => (
                <div
                    key={point.dataKey}
                    style={{
                        color: point.stroke,
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 20,
                    }}
                >
                    <span>{point.name}</span>
                    <strong>{formatInt(point.value)}</strong>
                </div>
            ))}
        </div>
    );
}

export default function CaseTrendByManagedBy() {
    const [typeOptions, setTypeOptions] = useState([]);
    const [selectedType, setSelectedType] = useState("");

    const [loadingTypes, setLoadingTypes] = useState(true);
    const [loadingChart, setLoadingChart] = useState(false);

    const [error, setError] = useState("");
    const [payloadByType, setPayloadByType] = useState(null);

    useEffect(() => {
        let cancelled = false;

        const loadTypes = async () => {
            try {
                setLoadingTypes(true);
                setError("");

                const response = await fetchWithAuth(
                    `${API_BASE}/ainstein/in/dashboard?chart_key=cases_by_type`
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const payload = await response.json();
                const options = normalizeTypeOptions(payload);

                if (!cancelled) {
                    setTypeOptions(options);
                    setSelectedType(options[0] || "");
                    setLoadingTypes(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || "Failed to load remediation types");
                    setTypeOptions([]);
                    setSelectedType("");
                    setLoadingTypes(false);
                }
            }
        };

        loadTypes();

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const loadChart = async () => {
            try {
                setLoadingChart(true);
                setError("");

                const response = await fetchWithAuth(
                    `${API_BASE}/ainstein/in/dashboard?chart_key=case_managed_by_trend_by_type`
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const payload = await response.json();

                if (!cancelled) {
                    setPayloadByType(payload);
                    setLoadingChart(false);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(err?.message || "Failed to load managed by trend");
                    setPayloadByType(null);
                    setLoadingChart(false);
                }
            }
        };

        loadChart();

        return () => {
            cancelled = true;
        };
    }, []);

    const { rows, managedByNames } = useMemo(() => {
        const slice = payloadByType?.[selectedType];

        if (!slice) {
            return { rows: [], managedByNames: [] };
        }

        return normalizePayload(slice);
    }, [payloadByType, selectedType]);

    const visibleManagedByNames = useMemo(() => {
        const order = ["ENOW", "FG", "FIELDGLASS"];

        return [...managedByNames].sort((a, b) => {
            const indexA = order.indexOf(safeKey(a));
            const indexB = order.indexOf(safeKey(b));

            if (indexA === -1 && indexB === -1) return String(a).localeCompare(String(b));
            if (indexA === -1) return 1;
            if (indexB === -1) return -1;

            return indexA - indexB;
        });
    }, [managedByNames]);

    const loading = loadingTypes || loadingChart;

    return (
        <section className="remediation-dashboard-section">
            <article className="remediation-dashboard-card">
                <div className="remediation-dashboard-card-headline">
                    <div>
                        <h3 className="remediation-dashboard-card-title">
                            Open / Closed Cases by Managed By
                        </h3>
                        <p className="remediation-dashboard-card-subtitle">
                            Detail view for selected remediation type
                        </p>
                    </div>

                    {typeOptions.length > 0 && (
                        <label className="remediation-dashboard-control">
                            <span>Remediation Type</span>
                            <select
                                value={selectedType}
                                onChange={(event) => setSelectedType(event.target.value)}
                            >
                                {typeOptions.map((typeName) => (
                                    <option key={typeName} value={typeName}>
                                        {typeName}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}
                </div>

                {loading && (
                    <div className="remediation-dashboard-loading">
                        <span className="remediation-dashboard-loading-spinner" />
                        <span>Loading data...</span>
                    </div>
                )}

                {!loading && (error || rows.length === 0) && (
                    <div className="remediation-dashboard-empty-state">
                        {error || "No managed by trend data available"}
                    </div>
                )}

                {!loading && !error && rows.length > 0 && (
                    <MeasuredChartFrame height={420}>
                        {({ width, height }) => (
                            <LineChart
                                width={width}
                                height={height}
                                data={rows}
                                margin={{ top: 8, right: 24, left: 0, bottom: 0 }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    stroke="rgba(255,255,255,0.06)"
                                />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={formatDate}
                                    tick={TICK}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    tick={TICK}
                                    axisLine={false}
                                    tickLine={false}
                                    width={56}
                                    tickFormatter={formatInt}
                                />
                                <Tooltip
                                    cursor={{ stroke: "rgba(80,178,255,0.25)", strokeWidth: 1 }}
                                    content={<CustomTooltip />}
                                />
                                <Legend
                                    wrapperStyle={{
                                        fontSize: 12,
                                        color: "#d6d5ea",
                                        paddingTop: 8,
                                    }}
                                    formatter={(value) => (
                                        <span style={{ color: "#e5e8f3" }}>{value}</span>
                                    )}
                                />

                                <Line
                                    type="monotone"
                                    dataKey="OPEN__TOTAL"
                                    name="Total Open"
                                    stroke={COLORS.TOTAL}
                                    strokeWidth={2.8}
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                    connectNulls
                                />

                                <Line
                                    type="monotone"
                                    dataKey="CLOSED__TOTAL"
                                    name="Total Closed"
                                    stroke={COLORS.TOTAL}
                                    strokeWidth={2.8}
                                    strokeDasharray="6 5"
                                    dot={false}
                                    activeDot={{ r: 5 }}
                                    connectNulls
                                />

                                {visibleManagedByNames.map((managedBy) => {
                                    const key = safeKey(managedBy);
                                    const color = getSeriesColor(managedBy);

                                    return (
                                        <Fragment key={managedBy}>
                                            <Line
                                                type="monotone"
                                                dataKey={`OPEN__${key}`}
                                                name={`${managedBy} Open`}
                                                stroke={color}
                                                strokeWidth={2}
                                                dot={false}
                                                activeDot={{ r: 4 }}
                                                connectNulls
                                            />
                                            <Line
                                                type="monotone"
                                                dataKey={`CLOSED__${key}`}
                                                name={`${managedBy} Closed`}
                                                stroke={color}
                                                strokeWidth={2}
                                                strokeDasharray="6 5"
                                                dot={false}
                                                activeDot={{ r: 4 }}
                                                connectNulls
                                            />
                                        </Fragment>
                                    );
                                })}
                            </LineChart>
                        )}
                    </MeasuredChartFrame>
                )}
            </article>
        </section>
    );
}